// Port of new arty's components/arty/community/AskExample.tsx (arty repo).
// Client divergences: copy is `arty-card` `community-ask.example.*`, the collapsible is
// @artstorefronts/ui's, and bar widths are literal Tailwind classes, not inline styles.
import type { ComponentType } from 'react';
import { BarChart3, ChevronDown, DollarSign, MessageSquare, Star, type LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@artstorefronts/ui';

import type { CommunityKind } from '~/lib/arty-actions/catalog';

/**
 * The lead-in every ask form opens with: one bold, plain line saying what the artist
 * actually gets back, and a "See what your results will look like" disclosure over a
 * worked example of the finished post. Every number and handle is illustrative and
 * static - no data, no fetch. It exists because "ask for a rating" means nothing until
 * you have seen what comes back.
 */

const ICONS: Record<CommunityKind, LucideIcon> = {
  critique: MessageSquare,
  poll: BarChart3,
  pricing: DollarSign,
  rating: Star,
};

type Row = { label: string; percent: number; width: string; winner?: boolean };

function DistributionRow({ row }: { row: Row }) {
  const { t } = useTranslation('arty-card');
  return (
    <div className="flex items-center gap-2 text-xs text-foreground">
      <span className="w-28 shrink-0 truncate">{row.label}</span>
      <span className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
        <span className={`block h-full rounded-full bg-green-500 ${row.width}`} />
      </span>
      <span className="w-20 shrink-0 text-right tabular-nums text-muted-foreground">
        {row.percent}%{row.winner ? ` ${t('community-ask.example.winner')}` : ''}
      </span>
    </div>
  );
}

function Quote({ handle, text }: { handle: string; text: string }) {
  return (
    <figure className="rounded-xl border border-border bg-background p-3">
      <figcaption className="text-xs font-medium text-foreground">{handle}</figcaption>
      <blockquote className="mt-1 text-xs leading-relaxed text-foreground">{text}</blockquote>
    </figure>
  );
}

function Tally({ text }: { text: string }) {
  return <p className="text-xs text-muted-foreground">{text}</p>;
}

function RatingExample() {
  const { t } = useTranslation('arty-card');
  const rows: Row[] = [
    { label: '9-10', percent: 45, width: 'w-[45%]' },
    { label: '7-8', percent: 38, width: 'w-[38%]' },
    { label: '5-6', percent: 13, width: 'w-[13%]' },
    { label: '1-4', percent: 4, width: 'w-[4%]' },
  ];
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-baseline gap-2">
        <span className="text-3xl font-semibold text-foreground">8.2</span>
        <span className="text-sm text-muted-foreground">/ 10</span>
        <span className="ml-auto text-xs text-muted-foreground">
          {t('community-ask.example.ratings', { total: 47 })}
        </span>
      </div>
      <div className="flex flex-col gap-1.5">
        {rows.map((row) => (
          <DistributionRow key={row.label} row={row} />
        ))}
      </div>
    </div>
  );
}

function CritiqueExample() {
  const { t } = useTranslation('arty-card');
  return (
    <div className="flex flex-col gap-2">
      <Tally text={t('community-ask.example.critiques', { total: 12 })} />
      <Quote handle="@maya_paints" text={t('community-ask.example.critique-one')} />
      <Quote handle="@j.rivera.art" text={t('community-ask.example.critique-two')} />
    </div>
  );
}

function PriceRow({ handle, price }: { handle: string; price: string }) {
  return (
    <div className="flex items-center justify-between rounded-lg bg-background px-3 py-2">
      <dt className="font-medium text-foreground">{handle}</dt>
      <dd className="tabular-nums text-foreground">{price}</dd>
    </div>
  );
}

function PricingExample() {
  const { t } = useTranslation('arty-card');
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-semibold text-foreground">$450 - $700</span>
        <span className="ml-auto text-xs text-muted-foreground">
          {t('community-ask.example.responses', { total: 23 })}
        </span>
      </div>
      <dl className="flex flex-col gap-1.5 text-xs">
        <PriceRow handle="@gallery_ren" price="$650" />
        <PriceRow handle="@sculpt.sam" price="$500" />
      </dl>
    </div>
  );
}

function PollExample() {
  const { t } = useTranslation('arty-card');
  const rows: Row[] = [
    { label: t('community-ask.example.poll-one'), percent: 58, width: 'w-[58%]', winner: true },
    { label: t('community-ask.example.poll-two'), percent: 28, width: 'w-[28%]' },
    { label: t('community-ask.example.poll-three'), percent: 14, width: 'w-[14%]' },
  ];
  return (
    <div className="flex flex-col gap-3">
      <Tally text={t('community-ask.example.votes', { total: 65 })} />
      <div className="flex flex-col gap-1.5">
        {rows.map((row) => (
          <DistributionRow key={row.label} row={row} />
        ))}
      </div>
    </div>
  );
}

const EXAMPLES: Record<CommunityKind, ComponentType> = {
  rating: RatingExample,
  critique: CritiqueExample,
  pricing: PricingExample,
  poll: PollExample,
};

export function AskExample({ kind }: { kind: CommunityKind }) {
  const { t } = useTranslation('arty-card');
  const Example = EXAMPLES[kind];
  const Icon = ICONS[kind];
  return (
    <Collapsible
      className="flex flex-col gap-2 rounded-xl border border-green-200 bg-green-50/50 p-3"
      data-ask-outcome=""
    >
      <div className="flex items-start gap-2.5">
        <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-green-50 text-green-600">
          <Icon aria-hidden="true" className="size-4" />
        </span>
        <p className="text-sm font-semibold leading-snug text-foreground">{t(`community-ask.kinds.${kind}.outcome`)}</p>
      </div>
      <CollapsibleTrigger className="group inline-flex min-h-11 items-center gap-1 self-start px-1 text-xs font-semibold text-green-600 hover:underline">
        {t('community-ask.example.toggle')}
        <ChevronDown aria-hidden="true" className="size-3.5 transition-transform group-data-[state=open]:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="flex flex-col gap-3 rounded-lg border border-border bg-background p-3">
          <p className="font-mono text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
            {t('community-ask.example.eyebrow')}
          </p>
          <Example />
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
